import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { 
  InAppNotification, 
  NotificationPreferences, 
  FCMPayload, 
  NotificationData 
} from '../types/Activity';
import { useAuth } from '../hooks/useAuth';
import { 
  getFCMToken, 
  onForegroundMessage 
} from '../config/firebase';
import {
  registerFCMToken,
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getNotificationStats,
  fetchNotificationPreferences,
  updateNotificationPreferences
} from '../services/api';

interface NotificationStats {
  total_notifications: number;
  unread_count: number;
  today_count: number;
  this_week_count: number;
}

interface NotificationContextType {
  // State
  notifications: InAppNotification[];
  unreadCount: number;
  loading: boolean;
  permissionStatus: NotificationPermission;
  fcmToken: string | null;
  preferences: NotificationPreferences | null;
  stats: NotificationStats | null;
  
  // Actions
  requestPermission: () => Promise<boolean>;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotificationById: (notificationId: number) => Promise<void>;
  updatePreferences: (prefs: NotificationPreferences) => Promise<void>;
  handleNotificationClick: (notification: InAppNotification) => void;
  
  // FCM
  initializeFCM: () => Promise<void>;
  
  // Service worker communication
  handleServiceWorkerMessage: (message: { type: string; activityId?: string }) => void;
  
  // Smart notification handling
  setNotificationClickHandler: (handler: (data: NotificationData) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
  onNavigateToActivity?: (activityId: number) => void;
  onNotificationClick?: (notificationData: NotificationData) => void;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  onNavigateToActivity,
  onNotificationClick
}) => {
  const { user } = useAuth();
  
  // State
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [smartNotificationHandler, setSmartNotificationHandler] = useState<((data: NotificationData) => void) | null>(null);

  // Request notification permission
  const requestPermission = async (): Promise<boolean> => {
    try {
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return false;
      }

      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        console.log('✅ Notification permission granted');
        await initializeFCM();
        return true;
      } else {
        console.log('❌ Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  // Initialize FCM
  const initializeFCM = async (): Promise<void> => {
    try {
      if (!user || permissionStatus !== 'granted') {
        return;
      }

      console.log('🔄 Initializing FCM...');
      
      // Get FCM token
      const token = await getFCMToken();
      if (token) {
        setFcmToken(token);
        
        // Register token with backend
        await registerFCMToken(token);
        console.log('✅ FCM token registered with backend');
      }

      // Set up foreground message listener
      onForegroundMessage((payload) => {
        console.log('📨 Foreground notification received:', payload);
        handleForegroundNotification(payload as unknown as FCMPayload);
      });
    } catch (error) {
      console.error('❌ Error initializing FCM:', error);
    }
  };

  // Handle foreground notifications
  const handleForegroundNotification = (payload: FCMPayload) => {
    const { notification, data } = payload;
    
    // Show browser notification if permission is granted and Notification API is available
    if (permissionStatus === 'granted' && notification && data && typeof Notification !== 'undefined') {
      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.body,
          icon: notification.icon || '/treffpunkslogo.svg',
          badge: notification.badge || '/treffpunkslogo.svg',
          data: data,
          tag: `connectandgo-${data.type || 'notification'}-${data.activity_id || 'unknown'}`,
          requireInteraction: shouldRequireInteraction(data.type || 'activity_reminder'),
        });

        browserNotification.onclick = () => {
          // Use the smart notification handler if available
          if (smartNotificationHandler) {
            smartNotificationHandler(data);
          } else if (onNotificationClick) {
            onNotificationClick(data);
          } else {
            handleNotificationDataClick(data);
          }
          browserNotification.close();
        };
      } catch (error) {
        console.log('ℹ️ Browser notifications not supported on this device:', error);
      }
    } else {
      console.log('ℹ️ Browser notifications not available - using in-app notifications only');
    }

    // Refresh in-app notifications
    refreshNotifications();
  };

  // Determine if notification should require interaction
  const shouldRequireInteraction = (type: string): boolean => {
    const importantTypes = [
      'activity_cancelled',
      'activity_invitation',
      'participation_approved',
      'participation_rejected'
    ];
    return importantTypes.includes(type);
  };

  // Handle notification click
  const handleNotificationDataClick = (data: NotificationData) => {
    if (!data || !data.activity_id) {
      console.warn('Invalid notification data:', data);
      return;
    }
    
    const activityId = parseInt(data.activity_id);
    
    switch (data.type) {
      case 'activity_invitation':
      case 'activity_updated':
      case 'activity_cancelled':
      case 'activity_reminder':
      case 'participation_approved':
      case 'participation_rejected':
      case 'organizer_message':
        if (onNavigateToActivity && activityId) {
          onNavigateToActivity(activityId);
        }
        break;
      default:
        console.log('Unknown notification type:', (data as { type: string }).type);
    }
  };

  // Handle clicks on in-app notifications
  const handleNotificationClick = (notification: InAppNotification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Backend notifications use object_id to reference the related item (usually activity)
    if (notification.object_id && onNavigateToActivity) {
      console.log(`📍 Navigating to activity ${notification.object_id} from notification`);
      onNavigateToActivity(notification.object_id);
    } else {
      console.log('⚠️ Notification has no object_id or navigation handler');
    }
  };

  // Refresh notifications from API
  const refreshNotifications = async (): Promise<void> => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Fetch notifications - this endpoint exists
      const notificationsData = await fetchNotifications(1, false);
      setNotifications(notificationsData.results);
      console.log(`✅ Fetched ${notificationsData.results.length} notifications`);
      
      // Always calculate unread count from the actual notifications first and trust local state
      const localUnreadCount = notificationsData.results.filter(n => !n.read).length;
      setUnreadCount(localUnreadCount);
      
      // Try to fetch stats but handle 404 gracefully
      try {
        const statsData = await getNotificationStats();
        setStats(statsData);
        if (statsData.unread_count !== localUnreadCount) {
          console.log(`ℹ️ Stats unread (${statsData.unread_count}) differs from local (${localUnreadCount}). Using local for UI.`);
        }
      } catch (statsError: unknown) {
        if (statsError instanceof Error && statsError.message?.includes('404')) {
          // Endpoint not implemented yet - use local calculation
          const defaultStats = {
            total_notifications: notificationsData.results.length,
            unread_count: localUnreadCount,
            today_count: 0,
            this_week_count: 0
          };
          setStats(defaultStats);
          console.log(`ℹ️ Using fallback notification stats (stats endpoint not available). Unread count: ${localUnreadCount}`);
        } else {
          console.error('❌ Error fetching notification stats:', statsError);
          // Fallback to local calculation even on other errors
          console.log(`ℹ️ Using local unread count calculation due to stats error: ${localUnreadCount}`);
        }
      }
      
    } catch (error) {
      console.error('❌ Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: number): Promise<void> => {
    try {
      await markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Refresh from server to ensure accurate count
      setTimeout(() => {
        refreshNotifications();
      }, 100);
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async (): Promise<void> => {
    try {
      const result = await markAllNotificationsAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      
      setUnreadCount(0);
      console.log(`✅ Marked ${result.affected_count} notifications as read`);
      
      // Refresh from server to ensure accurate count
      setTimeout(() => {
        refreshNotifications();
      }, 100);
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotificationById = async (notificationId: number): Promise<void> => {
    try {
      await deleteNotification(notificationId);
      
      // Update local state atomically and sync unread count from the updated list
      setNotifications(prev => {
        const updated = prev.filter(notif => notif.id !== notificationId);
        const newUnread = updated.filter(n => !n.read).length;
        setUnreadCount(newUnread);
        return updated;
      });

      // Refresh from server to ensure full sync (especially if backend performs batch logic)
      setTimeout(() => {
        refreshNotifications();
      }, 100);
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
    }
  };

  // Update notification preferences
  const updatePreferences = async (prefs: NotificationPreferences): Promise<void> => {
    try {
      const updatedPrefs = await updateNotificationPreferences(prefs);
      setPreferences(updatedPrefs);
      console.log('✅ Notification preferences updated');
    } catch (error: unknown) {
      if (error instanceof Error && error.message?.includes('404')) {
        // Endpoint not implemented yet - store locally
        setPreferences(prefs);
        console.log('ℹ️ Preferences stored locally (update endpoint not available)');
      } else {
        console.error('❌ Error updating notification preferences:', error);
      }
    }
  };

  // Load notification preferences
  const loadPreferences = async (): Promise<void> => {
    if (!user) return;

    try {
      const prefs = await fetchNotificationPreferences();
      setPreferences(prefs);
    } catch (error: unknown) {
      if (error instanceof Error && error.message?.includes('404')) {
        // Endpoint not implemented yet - use default preferences
        const defaultPrefs = {
          push_notifications: true,
          email_notifications: true,
          participation_updates: true,
          activity_reminders: true,
          organizer_messages: true,
          activity_invitations: true
        };
        setPreferences(defaultPrefs);
        console.log('ℹ️ Using default notification preferences (preferences endpoint not available)');
      } else {
        console.error('❌ Error loading notification preferences:', error);
      }
    }
  };

  // Handle service worker messages
  const handleServiceWorkerMessage = useCallback((message: { type: string; activityId?: string }) => {
    console.log('📨 Service worker message received:', message);
    
    switch (message.type) {
      case 'NAVIGATE_TO_ACTIVITY':
        if (onNavigateToActivity && message.activityId) {
          onNavigateToActivity(parseInt(message.activityId));
        }
        break;
      case 'AUTO_REGISTER_ACTIVITY':
        // Handle auto-registration from notification action
        console.log('Auto-register for activity:', message.activityId);
        // This could trigger automatic registration logic
        break;
      case 'OPEN_MESSAGE_COMPOSER':
        // Handle opening message composer
        console.log('Open message composer for activity:', message.activityId);
        break;
      default:
        console.log('Unknown service worker message type:', message.type);
    }
  }, [onNavigateToActivity]);

  // Set up service worker message listener
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, [handleServiceWorkerMessage]);

  // Set notification click handler
  const setNotificationClickHandler = (handler: (data: NotificationData) => void) => {
    setSmartNotificationHandler(() => handler);
  };

  // Recalculate unread count whenever notifications change
  useEffect(() => {
    const localUnreadCount = notifications.filter(n => !n.read).length;
    // Only update if there's a significant discrepancy to avoid unnecessary re-renders
    if (Math.abs(unreadCount - localUnreadCount) > 0) {
      console.log(`🔄 Syncing unread count: ${unreadCount} → ${localUnreadCount}`);
      setUnreadCount(localUnreadCount);
    }
  }, [notifications, unreadCount]);

  // Initialize on user login
  useEffect(() => {
    if (user) {
      // Check current permission status
      if ('Notification' in window) {
        setPermissionStatus(Notification.permission);
        
        // Initialize FCM if permission is already granted
        if (Notification.permission === 'granted') {
          initializeFCM();
        }
      }
      
      // Load preferences and notifications
      loadPreferences();
      refreshNotifications();
    } else {
      // Clear state on logout
      setNotifications([]);
      setUnreadCount(0);
      setFcmToken(null);
      setPreferences(null);
      setStats(null);
    }
  }, [user]);

  // Refresh notifications periodically (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshNotifications();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user]);

  const value: NotificationContextType = {
    // State
    notifications,
    unreadCount,
    loading,
    permissionStatus,
    fcmToken,
    preferences,
    stats,
    
    // Actions
    requestPermission,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
    updatePreferences,
    handleNotificationClick,
    
    // FCM
    initializeFCM,
    
    // Service worker
    handleServiceWorkerMessage,
    
    // Smart notification handling
    setNotificationClickHandler,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 